// =============================================================================
// ui/state/supabase.tsx — the client, and the signed-in user, as context.
// -----------------------------------------------------------------------------
// Added when the Trades tab began issuing its OWN read (D57): the table's rows
// are scoped to the active date range, which the boot projection cannot serve,
// so a tab now needs the client rather than only the loaded data.
//
// Context rather than a prop on every tab. Prop-drilling `sb` through each tab
// signature is the same wiring repeated per file, and the version that gets
// forgotten is a tab that quietly reads from a DIFFERENT client than the rest of
// the app — which under an auth refresh means one screen holding a stale token.
//
// ⚠ Deliberately carries `userId` too. Every user-scoped read is filtered by RLS
// rather than by a where-clause, so the id is rarely needed — but where it IS
// needed (preferences upsert), taking it from the same place as the client
// guarantees they refer to the same session.
// =============================================================================

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

export interface SupabaseState {
  sb: SupabaseClient;
  userId: string;
}

const Ctx = createContext<SupabaseState | null>(null);

export function useSupabase(): SupabaseState {
  const v = useContext(Ctx);
  // A tab rendering outside the provider would otherwise read null and, at best,
  // throw somewhere further from the cause.
  if (!v) throw new Error('useSupabase() outside <SupabaseProvider>');
  return v;
}

export function SupabaseProvider(
  { sb, userId, children }: { sb: SupabaseClient; userId: string; children: ReactNode },
) {
  const value = useMemo<SupabaseState>(() => ({ sb, userId }), [sb, userId]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
